import { exists, phoneFormatter } from '@treasury/utils';
import type { TextFieldValueChangedEvent, TextField as VaadinTextField } from '@vaadin/text-field';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ref } from 'lit/directives/ref.js';
import { phoneIcon } from '../assets/icons';
import { TmTextField } from './tm-text-field';

// eslint-disable-next-line no-duplicate-imports
import '@vaadin/text-field';

const allowedCharacters = '[0-9]';
const allowedRegex = new RegExp(`^${allowedCharacters}+$`, 'i');

export const tagName = 'tm-phone-field';
@customElement(tagName)
export class TmPhoneField extends TmTextField {
    @property()
    public initialFormattedValue = '';

    public connectedCallback(): void {
        super.connectedCallback();
        this.listenForValueChange(({ value }) => {
            this.setInnerValue(value);
        });
    }

    protected validate() {
        if (!this.value) {
            return false;
        }

        const errors: string[] = [];
        if (this.value.length !== 10) {
            errors.push('Phone number must be 10 digits long.');
        }

        if (!allowedRegex.test(this.value)) {
            errors.push('Phone number must only contain digits.');
        }

        if (errors.length > 0) {
            return errors;
        }

        if (this.inputField) {
            return (this.inputField as VaadinTextField).validate();
        }

        return true;
    }

    protected render() {
        const errorMessage = this.errorMessages?.join('\r\n');

        return html`
            <vaadin-text-field
                ${ref(this.inputFieldRef)}
                @value-changed=${(e: TextFieldValueChangedEvent) => this.onValueChanged(e)}
                class="w-full"
                part="component"
                manual-validation
                ?invalid="${!this.isValid}"
                error-message=${ifDefined(errorMessage)}
                .label=${this.label}
                .value=${this.initialFormattedValue}
                placeholder=${ifDefined(this.placeholder)}
                pattern=${ifDefined(this.pattern)}
                allowed-char-pattern=${allowedCharacters}
                helper-text=${ifDefined(this.helperText)}
                .minlength=${this.minLength}
                .maxlength=${this.maxLength || 14}
                ?required=${this.required}
                ?readonly=${this.readonly}
                ?disabled=${this.disabled}
            >
                <div slot="prefix" class="mt-1 mr-1 z-10">
                    <tm-icon
                        .icon=${phoneIcon}
                        height="22px"
                        width="22px"
                        color="var(--tm-color-gray-medium)"
                    ></tm-icon>
                </div>
            </vaadin-text-field>
        `;
    }

    private onValueChanged(e: TextFieldValueChangedEvent) {
        const { value } = e.detail;
        const digits = normalizeInput(value);
        this.changeValue(digits.length > 0 ? digits : undefined);
        this.initialFormattedValue = phoneFormatter(this.initial);
    }

    /**
     * Android doesn't always respect the allow-char-pattern,
     * so this is used to force the formattedValue value and prevent non-digits characters
     */
    private setInnerValue(value?: string) {
        const input = this.shadowRoot?.querySelector('vaadin-text-field input') as HTMLInputElement;
        if (input) {
            input.value = phoneFormatter(value);
        }
    }

    public static get styles() {
        return [
            ...super.styles,
            css`
                *::part(label) {
                    left: 36px;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmPhoneField;
    }
}

/**
 *
 * @param value An input string possibly formatted as a phone number.
 * @returns The original input stripped of dashes and other delimiters.
 */
function normalizeInput(value?: string) {
    if (!exists(value)) {
        return '';
    }

    if (typeof value !== 'string') {
        throw new Error('Cannot normalize input. It is not a string.');
    }

    return value.replace(/[^\d]/g, '');
}
